/**
 * Unified Results Panel - Context-Aware Results Display Component
 * 
 * A flexible, pluggable panel that adapts to different contexts:
 * - Spots Tab: Enhanced search across cases with evidence/content matching
 * - Similar Jobs: Content similarity within user's work
 * - Cross-Case: Investigation patterns across multiple cases
 * - Timeline: Temporal analysis of related jobs
 * 
 * Features:
 * - Context-driven UI configuration
 * - Pluggable search providers
 * - Flexible filtering and sorting
 * - Can be used inline or wrapped in a modal
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FaFilter, FaSync, FaChevronDown } from 'react-icons/fa';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import UnifiedSimilarResults from '../display/UnifiedSimilarResults';
import UniversalEvidenceWindow from '../../evidence/UniversalEvidenceWindow';
import { getEvidenceActions } from '../../evidence/evidenceActionConfigs';
import { EvidenceResultsModal } from '../../modals/results/wrappers/CleanResultsModals';
import { getContentTypeFromFileName } from '../../../utils/fileTypeUtils.js';
import JobCard from '../display/cards/JobCard';
import './UnifiedResultsPanel.css';

const UnifiedResultsPanel = ({
  context,
  searchProvider,
  data = {},
  className = '',
  onResultSelect,
  showHeader = true,
  autoSearch = false
}) => {
  // Panel state
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState({});
  const [activeFilters, setActiveFilters] = useState({});
  const [sortBy, setSortBy] = useState('relevance');
  const [viewMode, setViewMode] = useState('grid');

  // Search function - defined before useEffect hooks that use it
  const handleSearch = useCallback(async () => {
    if (!searchProvider || isSearching) return;

    setIsSearching(true);
    setSearchResults([]);

    try {
      console.log(`🔍 ${context.type}: Starting search with criteria:`, searchCriteria);
      
      const results = await searchProvider.search(searchCriteria, data);
      
      console.log(`✅ ${context.type}: Found ${results.length} results`);
      setSearchResults(results);
      setHasSearched(true);
    } catch (error) {
      console.error(`❌ ${context.type}: Search failed:`, error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [searchProvider, searchCriteria, data, context.type, isSearching]);

  // Initialize search criteria from context defaults
  useEffect(() => {
    if (searchProvider && searchProvider.getDefaultCriteria) {
      const defaultCriteria = searchProvider.getDefaultCriteria();
      setSearchCriteria(defaultCriteria);
    }
  }, [searchProvider]);

  // Auto-search when enabled and criteria are ready
  useEffect(() => {
    if (autoSearch && searchProvider && Object.keys(searchCriteria).length > 0 && !hasSearched) {
      // Initial search - run once when criteria are set
      handleSearch();
    }
  }, [autoSearch, searchProvider, searchCriteria, hasSearched, handleSearch]);

  // Auto-search when search criteria changes (after initial search)
  useEffect(() => {
    if (autoSearch && hasSearched && searchProvider && Object.keys(searchCriteria).length > 0) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCriteria]); // Only trigger on searchCriteria changes

  // Update search criteria
  const updateSearchCriteria = useCallback((updates) => {
    setSearchCriteria(prev => ({ ...prev, ...updates }));
  }, []);

  // Filter and sort results
  const processedResults = useMemo(() => {
    let filtered = searchResults;

    // Apply active filters
    Object.entries(activeFilters).forEach(([filterType, filterValue]) => {
      if (filterValue && searchProvider.applyFilter) {
        filtered = searchProvider.applyFilter(filtered, filterType, filterValue);
      }
    });

    // Apply sorting
    if (searchProvider.sortResults) {
      filtered = searchProvider.sortResults(filtered, sortBy);
    }

    return filtered;
  }, [searchResults, activeFilters, sortBy, searchProvider]);

  // Get statistics
  const statistics = useMemo(() => {
    if (searchProvider.getStatistics) {
      return searchProvider.getStatistics(searchResults);
    }
    return null;
  }, [searchResults, searchProvider]);

  // Handle result selection
  const handleResultSelect = useCallback((result) => {
    if (onResultSelect) {
      onResultSelect(result);
    }
  }, [onResultSelect]);

  return (
    <div className={`unified-results-panel ${className}`}>
      {/* Header */}
      {showHeader && (
        <div className="panel-header">
          <div className="header-content">
            <h2 className="panel-title">{context.title}</h2>
            {context.subtitle && (
              <p className="panel-subtitle">{context.subtitle}</p>
            )}
          </div>
        </div>
      )}

      {/* Search Controls */}
      {searchProvider && (
        <div className="search-controls-section">
          <SearchControls
            context={context}
            searchProvider={searchProvider}
            searchCriteria={searchCriteria}
            onUpdateCriteria={updateSearchCriteria}
          />
        </div>
      )}

      {/* Results Controls */}
      {hasSearched && (
        <div className="results-controls">
          <div className="results-info">
            <span className="results-count">
              {isSearching ? 'Searching...' : `${processedResults.length} results found`}
            </span>
          </div>
          
          <div className="results-actions">
            {/* Filters */}
            {searchProvider.getAvailableFilters && (
              <FilterControls
                availableFilters={searchProvider.getAvailableFilters()}
                activeFilters={activeFilters}
                onUpdateFilters={setActiveFilters}
                results={searchResults}
              />
            )}

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="relevance">Sort by Relevance</option>
              <option value="date">Sort by Date</option>
              <option value="similarity">Sort by Similarity</option>
              <option value="provider">Sort by Provider</option>
            </select>

            {/* View Mode */}
            <div className="view-mode-toggle">
              <button
                className={`btn-toggle ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
              >
                Grid
              </button>
              <button
                className={`btn-toggle ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                List
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results Display */}
      <div className="results-section">
        <ResultsDisplay
          results={processedResults}
          viewMode={viewMode}
          context={context}
          isSearching={isSearching}
          hasSearched={hasSearched}
          onResultSelect={handleResultSelect}
          data={data}
          statistics={statistics}
        />
      </div>
    </div>
  );
};

// Search Controls Component
const SearchControls = ({
  context,
  searchProvider,
  searchCriteria,
  onUpdateCriteria
}) => {
  const availableControls = searchProvider.getSearchControls ? searchProvider.getSearchControls() : [];
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = useRef({});

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && dropdownRefs.current[openDropdown] && 
          !dropdownRefs.current[openDropdown].contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  // Get the selected option for a control
  const getSelectedOption = (control) => {
    const value = searchCriteria[control.id];
    return control.options.find(opt => opt.value === value) || control.options[0];
  };

  // Get the combined description for the preview
  const getSearchDescription = () => {
    const descriptions = availableControls.map(control => {
      const selected = getSelectedOption(control);
      return selected.description || selected.label;
    });
    
    return `Searching for ${descriptions.join(' with ')}.`;
  };

  return (
    <div className="search-controls">
      {/* Horizontal Dropdowns */}
      <div className="search-parameters-row">
        {availableControls.map(control => {
          const selectedOption = getSelectedOption(control);
          const isOpen = openDropdown === control.id;
          
          return (
            <div key={control.id} className="search-parameter">
              <div className="parameter-label">{control.label}</div>
              <div 
                className="parameter-dropdown"
                ref={el => dropdownRefs.current[control.id] = el}
              >
                <button
                  className={`dropdown-button ${isOpen ? 'active' : ''}`}
                  onClick={() => setOpenDropdown(isOpen ? null : control.id)}
                >
                  <span>{selectedOption.label}</span>
                  <FaChevronDown className="dropdown-arrow" />
                </button>
                
                {isOpen && (
                  <div className="dropdown-menu">
                    {control.options.map(option => (
                      <div
                        key={option.value}
                        className={`dropdown-option ${option.value === selectedOption.value ? 'selected' : ''}`}
                        onClick={() => {
                          onUpdateCriteria({ [control.id]: option.value });
                          setOpenDropdown(null);
                        }}
                      >
                        <div className="dropdown-option-label">{option.label}</div>
                        {option.description && (
                          <div className="dropdown-option-description">{option.description}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Search Preview Box */}
      <div className="search-preview-box">
        <div className="preview-title">Search Configuration</div>
        <div className="preview-description">
          {getSearchDescription()}
        </div>
      </div>
    </div>
  );
};


// Filter Controls Component
const FilterControls = ({ availableFilters, activeFilters, onUpdateFilters, results }) => {
  return (
    <div className="filter-controls">
      <FaFilter />
      {availableFilters.map(filter => (
        <select
          key={filter.id}
          value={activeFilters[filter.id] || ''}
          onChange={(e) => onUpdateFilters(prev => ({
            ...prev,
            [filter.id]: e.target.value || undefined
          }))}
          className="filter-select"
        >
          <option value="">{filter.label}</option>
          {filter.options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ))}
    </div>
  );
};

// Statistics Display Component
const StatisticsDisplay = ({ statistics, context }) => {
  return (
    <div className="statistics-display">
      <h3>Search Statistics</h3>
      <div className="stats-grid">
        {Object.entries(statistics).map(([key, value]) => (
          <div key={key} className="stat-item">
            <span className="stat-label">{key}</span>
            <span className="stat-value">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Results Display Component
const ResultsDisplay = ({
  results,
  viewMode,
  context,
  isSearching,
  hasSearched,
  onResultSelect,
  data,
  statistics
}) => {
  const [activeTab, setActiveTab] = useState('jobs');

  // Extract evidence from results
  const evidenceList = useMemo(() => {
    if (!results || results.length === 0) return [];
    
    const evidenceMap = new Map();
    
    results.forEach(result => {
      if (result.evidenceFiles) {
        result.evidenceFiles.forEach(evidence => {
          const key = evidence.id || evidence.fileName || evidence.name;
          if (evidenceMap.has(key)) {
            evidenceMap.get(key).jobs.push({
              jobId: result.jobId,
              provider: result.provider,
              date: result.date,
              casefileId: result.casefileId
            });
          } else {
            evidenceMap.set(key, {
              ...evidence,
              jobs: [{
                jobId: result.jobId,
                provider: result.provider,
                date: result.date,
                casefileId: result.casefileId
              }]
            });
          }
        });
      }
    });
    
    // Build array and filter out evidence explicitly marked private
    const evidenceArray = Array.from(evidenceMap.values())
      .filter(ev => ev?.isPublic === true);
    console.log('Extracted evidence list:', evidenceArray.map(e => ({
      fileName: e.fileName,
      hasDownloadURL: !!e.downloadURL,
      downloadURL: e.downloadURL
    })));
    
    return evidenceArray;
  }, [results]);

  if (!hasSearched) {
    return (
      <div className="search-prompt">
        <p>Configure your search criteria and click "Search" to find related jobs.</p>
      </div>
    );
  }

  if (isSearching) {
    return (
      <div className="search-loading">
        <FaSync className="spinning" />
        <p>Searching for results...</p>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="no-results">
        <p>No results found with the current search criteria.</p>
        <p>Try adjusting your search parameters or filters.</p>
      </div>
    );
  }

  return (
    <div className="results-container">
      {/* Results Tabs */}
      <div className="results-tabs">
        <button
          className={`results-tab ${activeTab === 'jobs' ? 'active' : ''}`}
          onClick={() => setActiveTab('jobs')}
        >
          Jobs
          <span className="results-tab-count">{results.length}</span>
        </button>
        <button
          className={`results-tab ${activeTab === 'evidence' ? 'active' : ''}`}
          onClick={() => setActiveTab('evidence')}
        >
          Evidence
          <span className="results-tab-count">{evidenceList.length}</span>
        </button>
        {statistics && (
          <button
            className={`results-tab ${activeTab === 'statistics' ? 'active' : ''}`}
            onClick={() => setActiveTab('statistics')}
          >
            Statistics
          </button>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'jobs' && (
        <UnifiedSimilarResults
          results={results}
          loading={false}
          error={null}
          viewMode={viewMode}
          context={context?.type || 'spots'}
          onResultSelect={onResultSelect}
          title=""
          showHeader={false}
          showFilters={false}
          showStats={false}
          className="embedded-results"
        />
      )}

      {activeTab === 'evidence' && (
        <EvidenceList 
          evidenceList={evidenceList} 
          onEvidenceSelect={onResultSelect}
          currentUser={data.currentUserId ? { uid: data.currentUserId } : null}
          context={context}
          data={data}
        />
      )}

      {activeTab === 'statistics' && statistics && (
        <div className="statistics-panel">
          <StatisticsDisplay statistics={statistics} context={context} />
        </div>
      )}
    </div>
  );
};

// Evidence List Component - Tile-based like bulletin with expandable jobs
const EvidenceList = ({ evidenceList, onEvidenceSelect, currentUser, context, data }) => {
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [isEvidenceModalOpen, setIsEvidenceModalOpen] = useState(false);
  const [casefileNames, setCasefileNames] = useState({});
  const [expandedEvidence, setExpandedEvidence] = useState(new Set());
  const [expandAll, setExpandAll] = useState(false);

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Toggle expand/collapse for individual evidence
  const toggleEvidence = (evidenceId) => {
    setExpandedEvidence(prev => {
      const newSet = new Set(prev);
      if (newSet.has(evidenceId)) {
        newSet.delete(evidenceId);
      } else {
        newSet.add(evidenceId);
      }
      return newSet;
    });
  };

  // Handle expand all/collapse all
  const handleExpandAll = () => {
    if (expandAll) {
      setExpandedEvidence(new Set());
      setExpandAll(false);
    } else {
      const allIds = evidenceList.map(ev => ev.id || ev.fileName);
      setExpandedEvidence(new Set(allIds));
      setExpandAll(true);
    }
  };

  // Fetch casefile names for all unique casefileIds in the evidence list
  useEffect(() => {
    const fetchCasefileNames = async () => {
      // Extract all casefileIds from evidence
      const allCasefileIds = evidenceList
        .map(ev => ev.casefileId || ev.jobs?.[0]?.casefileId)
        .filter(id => id);

      const uniqueCasefileIds = [...new Set(allCasefileIds)];

      if (uniqueCasefileIds.length === 0) return;

      const names = { ...casefileNames };
      let fetchedCount = 0;
      
      for (const casefileId of uniqueCasefileIds) {
        // Skip if already fetched
        if (names[casefileId]) continue;

        try {
          const casefileRef = doc(db, 'casefiles', casefileId);
          const casefileSnap = await getDoc(casefileRef);
          
          if (casefileSnap.exists()) {
            const data = casefileSnap.data();
            // Check title first (primary field), then name as fallback
            names[casefileId] = data.title || data.name || `Case ${casefileId.substring(0, 8)}`;
            fetchedCount++;
          } else {
            names[casefileId] = `Case ${casefileId.substring(0, 8)}`;
          }
        } catch (error) {
          names[casefileId] = `Case ${casefileId.substring(0, 8)}`;
          console.error(`Error fetching casefile name for ${casefileId}:`, error);
        }
      }
      
      if (fetchedCount > 0 || Object.keys(names).length !== Object.keys(casefileNames).length) {
        setCasefileNames(names);
      }
    };

    if (evidenceList.length > 0) {
      fetchCasefileNames();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidenceList]); // Only re-run when evidenceList changes, not casefileNames (would cause infinite loop)

  // Using shared file type utility

  const handleEvidenceModalOpen = (evidence) => {
    setSelectedEvidence(evidence);
    setIsEvidenceModalOpen(true);
  };

  if (evidenceList.length === 0) {
    return (
      <div className="no-results">
        <p>No evidence files found in the search results.</p>
        <p>Evidence information may not be available for all jobs.</p>
      </div>
    );
  }

  return (
    <>
      {/* Expand All Button */}
      <div className="evidence-controls">
        <button className="expand-all-btn" onClick={handleExpandAll}>
          {expandAll ? '▲ Collapse All' : '▼ Expand All'}
        </button>
        <span className="evidence-controls-info">
          {evidenceList.length} evidence file{evidenceList.length !== 1 ? 's' : ''} with related jobs
        </span>
      </div>

      <div className="evidence-tiles-container">
        {evidenceList.map((evidence, index) => {
          // Use the evidence data directly - it should now have all properties including downloadURL
          const evidenceItem = {
            ...evidence, // Spread all evidence properties first
            id: evidence.id || `evidence-${index}`,
            fileName: evidence.fileName || evidence.name || 'Unknown File',
            name: evidence.fileName || evidence.name || 'Unknown File',
            fileSize: evidence.fileSize || evidence.size, // UniversalEvidenceWindow expects fileSize
            casefileId: evidence.casefileId || evidence.jobs?.[0]?.casefileId || null,
            createdAt: evidence.createdAt || evidence.uploadDate || new Date(), // UniversalEvidenceWindow expects createdAt for date display
            // Ensure contentType is set for proper media detection
            contentType: evidence.contentType || getContentTypeFromFileName(evidence.fileName || evidence.name),
            // Add job information as metadata
            relatedJobs: evidence.jobs || []
          };
          
          // Debug: Log what we're passing to UniversalEvidenceWindow
          console.log('Evidence item for display:', {
            fileName: evidenceItem.fileName,
            downloadURL: evidenceItem.downloadURL,
            contentType: evidenceItem.contentType,
            fileSize: evidenceItem.fileSize,
            hasURL: !!evidenceItem.downloadURL
          });

          // Generate action buttons for search results context (similar to casefile)
          const actionButtons = getEvidenceActions('casefile', {
            onOpenResults: () => {
              // Could open results for this evidence
              if (onEvidenceSelect) {
                onEvidenceSelect({ ...evidenceItem, type: 'results' });
              }
            },
            onOpenBounty: () => {
              // Could open bounty for this evidence
              if (onEvidenceSelect) {
                onEvidenceSelect({ ...evidenceItem, type: 'bounty' });
              }
            },
            onOpenScan: () => {
              // Open full evidence modal for AI analysis
              handleEvidenceModalOpen(evidenceItem);
            }
          });

          // Get the correct casefile name for this evidence
          const evidenceCasefileId = evidenceItem.casefileId || evidenceItem.jobs?.[0]?.casefileId;
          const evidenceCasefileName = casefileNames[evidenceCasefileId] || `Case ${evidenceCasefileId?.substring(0, 8) || 'Unknown'}`;

          const isExpanded = expandedEvidence.has(evidenceItem.id);
          const jobCount = evidenceItem.relatedJobs?.length || 0;

          return (
            <div key={evidenceItem.id} className="evidence-with-jobs-wrapper">
              {/* Evidence Tile with Job Badge */}
              <div className="evidence-card-container">
                <UniversalEvidenceWindow
                  evidence={evidenceItem}
                  mode="tile"
                  actionButtons={actionButtons}
                  onOpenModal={() => handleEvidenceModalOpen(evidenceItem)}
                  currentUser={currentUser}
                  formatFileSize={formatFileSize}
                  isSelected={false}
                  onSelectionChange={null}
                  casefileName={evidenceCasefileName}
                  casefileId={evidenceCasefileId}
                  // Summary props (not used in tile mode but required)
                  evidenceSummary={evidenceItem.summary || ''}
                  onSummaryChange={null}
                  isEditingSummary={false}
                  onEditingSummaryChange={null}
                  isSavingSummary={false}
                  onSavingSummaryChange={null}
                />
                
                {/* Job Count Badge and Expand Button */}
                {jobCount > 0 && (
                  <div className="evidence-jobs-toggle" onClick={() => toggleEvidence(evidenceItem.id)}>
                    <span className="jobs-badge">
                      📊 {jobCount} Job{jobCount !== 1 ? 's' : ''}
                    </span>
                    <span className="expand-indicator">
                      {isExpanded ? '▲ Collapse' : '▼ Expand'}
                    </span>
                  </div>
                )}
              </div>

              {/* Expandable Jobs Section */}
              {isExpanded && jobCount > 0 && (
                <div className="related-jobs-section">
                  <div className="related-jobs-header">Related Jobs:</div>
                  <div className="related-jobs-list">
                    {evidenceItem.relatedJobs.map((job, jobIndex) => {
                      // Construct a proper job object for JobCard
                      // The relatedJobs array contains { jobId, provider, date, casefileId }
                      // We need to create a job object that JobCard expects
                      const jobForCard = {
                        id: job.jobId,
                        provider: job.provider,
                        createdAt: job.date,
                        status: 'completed', // Assume completed since it has results
                        casefileId: job.casefileId,
                        evidenceId: evidenceItem.id
                      };
                      
                      return (
                        <JobCard
                          key={jobIndex}
                          job={jobForCard}
                          resultsMeta={{}}
                          size="regular"
                          onOpen={() => {
                            if (onEvidenceSelect) {
                              onEvidenceSelect({ ...job, type: 'job', evidenceFileName: evidenceItem.fileName });
                            }
                          }}
                          collapsible={false}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Evidence Modal for full view */}
      {selectedEvidence && (() => {
        const modalCasefileId = selectedEvidence.casefileId || selectedEvidence.jobs?.[0]?.casefileId;
        const modalCasefileName = casefileNames[modalCasefileId] || `Case ${modalCasefileId?.substring(0, 8) || 'Unknown'}`;
        
        return (
          <EvidenceResultsModal
            evidenceId={selectedEvidence.id}
            evidenceName={selectedEvidence.fileName || selectedEvidence.name}
            isOpen={isEvidenceModalOpen}
            onClose={() => {
              setIsEvidenceModalOpen(false);
              setSelectedEvidence(null);
            }}
            currentUser={currentUser}
            casefileId={modalCasefileId}
            casefileName={modalCasefileName}
            slideMode={true}
          />
        );
      })()}
    </>
  );
};

export default UnifiedResultsPanel;
